import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { showApiError, clearAuthStorage } from "@/utils";
import apiClient from "@/services/apiClient";

const usePostData = (url, initialRequestBody, options = {}) => {
  const [responseData, setResponseData] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const requestBodyRef = useRef(initialRequestBody);
  const navigate = useNavigate();
  const location = useLocation();

  const postData = async (newRequestBody = null) => {
    if (newRequestBody) {
      requestBodyRef.current = newRequestBody;
    }

    if (!requestBodyRef.current) return;

    setIsLoaded(false);
    setError(null);
    setResponseData([]);

    if (options.skip) {
      return;
    }

    try {
      const response = await apiClient.post(url, requestBodyRef.current, {
        headers: options.headers,
      });

      const response_json = response.data;
      const data = response_json ? response_json["data"] : [];

      setResponseData(data);
      setIsLoaded(true);
    } catch (err) {
      if (err.response?.status === 401) {
        console.warn("Unauthorized access - redirecting to login");
        clearAuthStorage();
        localStorage.setItem(
          "redirectAfterLogin",
          location.pathname + location.search,
        );
        navigate("/login");
        return;
      }
      console.error("Error posting data:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        `HTTP error! Status: ${err.response?.status}`;
      const errObj = new Error(errorMessage);
      showApiError(errorMessage);
      setError(errObj);
    }
  };

  useEffect(
    () => {
      requestBodyRef.current = initialRequestBody;
      postData(initialRequestBody);
    },
    // eslint-disable-next-line
    [url, JSON.stringify(initialRequestBody), options.skip],
  );

  return [responseData, isLoaded, error, postData];
};

export default usePostData;
